/**
 * WorkflowTypeBadge Component
 * Colored badge identifying the workflow type (Red Dot, Survey, CEU, etc.).
 */

import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { workflowTypeLabel } from './workflowTypeLabel';

export interface WorkflowTypeBadgeProps {
  workflowType: string;
}

interface WorkflowStyle {
  bg: string;
  color: string;
  label: string;
}

function getWorkflowStyle(workflowType: string): WorkflowStyle {
  const normalized = workflowType.toLowerCase().replace(/[-_\s]/g, '');
  const label = workflowTypeLabel(workflowType);

  if (normalized === 'reddot') {
    // success.bg is #f0fdf4 (lighter) — using Tailwind emerald-100 for stronger pill contrast
    return { bg: '#d1fae5', color: theme.colors.status.success.text, label };
  }

  if (normalized === 'qualitysurvey' || normalized === 'survey') {
    // Purple / violet — not in the theme palette; keeping hex values
    return { bg: '#ede9fe', color: '#5b21b6', label };
  }

  if (normalized === 'ceu') {
    // Blue (info-like) — not in the theme palette; keeping hex values
    return { bg: '#dbeafe', color: '#1e40af', label };
  }

  // Fallback: neutral gray — theme.colors.backgrounds.gray matches #f3f4f6
  return { bg: theme.colors.backgrounds.gray, color: theme.colors.text.secondary, label };
}

const Badge = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

export function WorkflowTypeBadge({ workflowType }: WorkflowTypeBadgeProps) {
  const { bg, color, label } = getWorkflowStyle(workflowType);

  return (
    <Badge $bg={bg} $color={color} aria-label={`Workflow type: ${label}`}>
      {label}
    </Badge>
  );
}

export default WorkflowTypeBadge;
